export const ALL_PROVIDER = "ALLPROVIDER";
export const ALL_GAME_TYPE = "allgametype";

const TYPE_ALIAS_GROUPS = {
  slot: ["slot", "slots"],
  casino: ["casino", "livecasino"],
  sport: ["sport", "sportbook", "sports"],
  card: ["card", "cardgames"],
  fishing: ["fishing"],
  keno: ["keno"],
  lotto: ["lotto"],
};

const TYPE_LABELS = {
  slot: "สล็อต",
  casino: "คาสิโนสด",
  sport: "กีฬา",
  card: "เกมไพ่",
  fishing: "ยิงปลา",
  keno: "หวย/คีโน่",
  lotto: "ลอตโต้",
};

function splitRestrictionValues(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeTypeToken(value) {
  const token = String(value || "")
    .trim()
    .toLowerCase();
  if (!token) {
    return "";
  }

  if (token === ALL_GAME_TYPE) {
    return ALL_GAME_TYPE;
  }

  const matchedType = Object.entries(TYPE_ALIAS_GROUPS).find(([, aliases]) =>
    aliases.includes(token),
  );

  return matchedType?.[0] || token;
}

export function createRestrictionState(currentUser) {
  const providerAllowanceRaw =
    currentUser?.gameProviderAvailable || ALL_PROVIDER;
  const providerAllowanceList =
    providerAllowanceRaw === ALL_PROVIDER
      ? null
      : splitRestrictionValues(providerAllowanceRaw).map((item) =>
          item.toLowerCase(),
        );

  const typeAllowanceList = currentUser?.gameTypeAvailable
    ? splitRestrictionValues(currentUser.gameTypeAvailable).map(
        normalizeTypeToken,
      )
    : [ALL_GAME_TYPE];

  const isProviderAllowed = (provider) => {
    if (!currentUser) {
      return true;
    }

    if (providerAllowanceList === null) {
      return true;
    }

    const candidates =
      typeof provider === "string"
        ? [provider]
        : [
            provider?.productId,
            provider?.game,
            provider?.productName,
            provider?.name,
            provider?.id,
          ];

    return candidates
      .filter(Boolean)
      .map((item) => String(item).toLowerCase())
      .some((item) => providerAllowanceList.includes(item));
  };

  const isTypeAllowed = (...types) => {
    if (typeAllowanceList.includes(ALL_GAME_TYPE)) {
      return true;
    }

    const normalizedTypes = types
      .flat()
      .map(normalizeTypeToken)
      .filter(Boolean);

    return normalizedTypes.some((type) => typeAllowanceList.includes(type));
  };

  return {
    providerAllowanceRaw,
    providerAllowanceList,
    typeAllowanceList,
    isProviderAllowed,
    isTypeAllowed,
  };
}

export function getBlockedTypeMessage(type) {
  const normalizedType = normalizeTypeToken(type);
  const label = TYPE_LABELS[normalizedType] || "ประเภทเกมส์นี้";
  return `ท่านไม่สามารถเล่นประเภท${label}ได้ เนื่องจากติดเงื่อนไข`;
}

export function getRestrictionSummary(currentUser, providerCatalog = []) {
  const restrictionState = createRestrictionState(currentUser);
  const hasTypeRestriction =
    !restrictionState.typeAllowanceList.includes(ALL_GAME_TYPE);
  const hasProviderRestriction =
    restrictionState.providerAllowanceList !== null;

  const typeLabels = hasTypeRestriction
    ? [...new Set(restrictionState.typeAllowanceList)]
        .map((type) => TYPE_LABELS[type] || type)
        .filter(Boolean)
    : [];

  const providerLookup = providerCatalog.reduce((accumulator, provider) => {
    const label =
      provider?.productName ||
      provider?.name ||
      provider?.label ||
      provider?.id;
    [provider?.productId, provider?.game, provider?.id, provider?.name].forEach(
      (key) => {
        if (key) {
          accumulator[String(key).toLowerCase()] = label;
        }
      },
    );
    return accumulator;
  }, {});

  const providerLabels = hasProviderRestriction
    ? [...new Set(restrictionState.providerAllowanceList)]
        .map((provider) => providerLookup[provider] || provider)
        .filter(Boolean)
    : [];

  return {
    hasRestrictions: hasTypeRestriction || hasProviderRestriction,
    typeLabels,
    providerLabels,
  };
}
